import { Auth } from 'aws-amplify';
import {
  AuthAdapter,
  SignInWithPasswordResult,
  SignInWithPasswordSuccess,
  Tokens,
} from '../../types';

type CognitoUser = import('amazon-cognito-identity-js').CognitoUser;
type CognitoUserSession = import('amazon-cognito-identity-js').CognitoUserSession;

export default class AmplifyAuthAdapter implements AuthAdapter {
  private pendingUser: CognitoUser | undefined;
  private pendingChallengeName: string | undefined;

  async signInWithPassword(
    email: string,
    password: string,
  ): Promise<SignInWithPasswordResult> {
    this.pendingUser = undefined;
    this.pendingChallengeName = undefined;

    let user: CognitoUser & {
      challengeName?: string;
      challengeParam?: Record<string, string>;
      Session?: string;
    };

    try {
      user = (await Auth.signIn(email, password)) as CognitoUser;
    } catch (error) {
      // If the user was deleted and re-created, the locally cached device key
      // is stale and Cognito responds with "Device does not exist."
      // Clear the cached device keys and retry the sign-in.
      if (
        (error as any)?.code === 'ResourceNotFoundException' ||
        (error as any)?.__type === 'ResourceNotFoundException' ||
        (error as any)?.name === 'ResourceNotFoundException'
      ) {
        this.clearCognitoDeviceKeys();
        user = (await Auth.signIn(email, password)) as CognitoUser;
      } else {
        throw error;
      }
    }

    if (
      user?.challengeName === 'SMS_MFA' ||
      user?.challengeName === 'SOFTWARE_TOKEN_MFA'
    ) {
      this.pendingUser = user;
      this.pendingChallengeName = user.challengeName;
      return {
        status: 'MFA_REQUIRED',
        challengeName: user.challengeName ?? 'SMS_MFA',
        challengeParameters: user.challengeParam ?? {},
        session: user.Session,
      };
    }
    if (user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
      this.pendingUser = user;
      this.pendingChallengeName = user.challengeName;
      return {
        status: 'CHPS_REQUIRED',
        challengeName: user.challengeName ?? 'CHPS_REQUIRED',
        challengeParameters: user.challengeParam ?? {},
        session: user.Session,
      };
    }

    return this.signInSuccess(await this.tokens());
  }

  /**
   * Removes locally cached Cognito device keys from localStorage.
   * These keys become stale when a user is deleted and re-created in Cognito.
   */
  private clearCognitoDeviceKeys(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    Object.keys(window.localStorage)
      .filter(
        key =>
          key.startsWith('CognitoIdentityServiceProvider.') &&
          (key.includes('.deviceKey') || 
           key.includes('.deviceGroupKey') ||
           key.includes('.randomPasswordKey')),
      )
      .forEach(key => window.localStorage.removeItem(key));
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async changePassword(
    email: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<SignInWithPasswordResult> {
    this.pendingUser = undefined;
    this.pendingChallengeName = undefined;
    const user = (await Auth.signIn(email, oldPassword)) as CognitoUser & {
      challengeName?: string;
      challengeParam?: Record<string, string>;
      Session?: string;
    };

    const result = await Auth.completeNewPassword(
      user,
      newPassword,
      user?.challengeParam?.requiredAttributes,
    );
    return this.signInSuccess(await this.tokens());
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async forgotPassword(username: string): Promise<Boolean> {
    const result = await Auth.forgotPassword(username);
    return true;
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async verifyAndResetPassword(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<Boolean> {
    const result = await Auth.forgotPasswordSubmit(username, code, newPassword);
    return true;
  }

  async signInWithMultiFactorAuth(verificationCode: string): Promise<Tokens> {
    if (!this.pendingUser) {
      throw new Error('No pending Cognito MFA challenge to confirm');
    }

    const challenge =
      this.pendingChallengeName === 'SMS_MFA' ||
      this.pendingChallengeName === 'SOFTWARE_TOKEN_MFA'
        ? this.pendingChallengeName
        : undefined;

    await Auth.confirmSignIn(this.pendingUser, verificationCode, challenge);

    this.pendingUser = undefined;
    this.pendingChallengeName = undefined;
    return this.tokens();
  }

  async signInWithCustomToken(
    token: string,
    username: string,
  ): Promise<Tokens> {
    if (!username) {
      throw new Error('Username required for custom token sign-in');
    }

    const sanitizedToken =
      token && token.startsWith('"') && token.endsWith('"')
        ? token.slice(1, -1)
        : token;

    const user = (await Auth.signIn(username)) as CognitoUser & {
      challengeName?: string;
    };

    const authenticatedUser = (await Auth.sendCustomChallengeAnswer(
      user,
      sanitizedToken,
      { source: 'web' },
    )) as CognitoUser & {
      challengeName?: string;
      signInUserSession?: CognitoUserSession | null;
    };

    if (authenticatedUser.challengeName) {
      throw new Error(
        `Unexpected additional challenge: ${authenticatedUser.challengeName}`,
      );
    }

    const session =
      authenticatedUser.signInUserSession || (await Auth.currentSession());

    return this.tokensFromSession(session);
  }

  async refreshTokens(_idToken: string): Promise<Tokens> {
    // v5 auto‑refreshes when calling currentSession()
    return this.tokens();
  }

  private async tokens(): Promise<Tokens> {
    const s = await Auth.currentSession(); // auto‑refreshes id/access tokens
    return this.tokensFromSession(s);
  }

  private tokensFromSession(session: CognitoUserSession): Tokens {
    const idToken = session.getIdToken();
    const accessToken = session.getAccessToken();
    const refreshToken = session.getRefreshToken();

    return {
      idToken: { token: idToken.getJwtToken(), payload: idToken.payload },
      accessToken: {
        token: accessToken.getJwtToken(),
        payload: accessToken.payload,
      },
      refreshToken: refreshToken.getToken(),
      expiresAt: idToken.getExpiration() * 1000,
    };
  }

  private signInSuccess(tokens: Tokens): SignInWithPasswordSuccess {
    return {
      status: 'SUCCESS',
      tokens,
    };
  }
}
